import { useEffect, useMemo, useRef, useState } from "react";

import { useCreateIdentity, useLookups } from "src/api/hooks";
import {
  Button,
  Combobox,
  Field,
  Input,
  RadioGroup,
  type RadioOption,
  Select,
  cn,
  humanPopulation
} from "src/components/ui";
import { DepartmentField } from "src/components/department-field";
import { PartnerDRIField } from "src/components/partner-dri-field";
import type { BeamType, Gender, Population } from "src/graphql/generated";
import { BEAM_TYPES, isBeamSubcategory } from "src/lib/beam";
import { assignableOrganizations } from "src/lib/partner-orgs";
import {
  FORMER_NAME_TYPES,
  type FormerNameRow,
  NAME_PROVENANCES,
  emptyFormerNameRow,
  formerNamesInput,
  rowsMissingAName
} from "src/lib/former-names";
import { InlineCreateOrg } from "src/screens/partner-orgs";
import type { Viewer } from "src/screens/types";

/**
 * Creating a person and their first engagement is one step, because an identity
 * without an engagement cannot exist. The form reflects that: there is no way to
 * submit only the top half.
 */
export function CreateIdentity({
  viewer,
  onCreated
}: {
  viewer: Viewer;
  onCreated: (id: string) => void;
}) {
  const create = useCreateIdentity();
  const { data: lookups } = useLookups();

  const writable = viewer.writablePopulations;
  const [population, setPopulation] = useState<Population | "">(writable[0] ?? "");
  const [legalFirstName, setLegalFirstName] = useState("");
  const [legalMiddleName, setLegalMiddleName] = useState("");
  const [legalLastName, setLegalLastName] = useState("");
  const [legalFullNameLocal, setLegalFullNameLocal] = useState("");
  const [preferredFirstName, setPreferredFirstName] = useState("");
  const [preferredMiddleName, setPreferredMiddleName] = useState("");
  const [preferredLastName, setPreferredLastName] = useState("");
  // "" is "not recorded". The enum has no empty member, so the submit below sends
  // undefined rather than an empty string.
  const [gender, setGender] = useState<Gender | "">("");
  // An ISO 3166-1 alpha-2 code, or "" for not recorded. The picker cannot hold
  // anything else.
  const [workLocation, setWorkLocation] = useState("");
  const [workEmail, setWorkEmail] = useState("");
  const [personalEmail, setPersonalEmail] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [entityId, setEntityId] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [partnerOrganizationId, setPartnerOrganizationId] = useState("");
  const [title, setTitle] = useState("");
  const [driId, setDriId] = useState("");
  const [contractLink, setContractLink] = useState("");
  // "" is "not recorded" for both, the way gender and work country already are.
  const [beamNumber, setBeamNumber] = useState("");
  const [beamType, setBeamType] = useState<BeamType | "">("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [formerNames, setFormerNames] = useState<FormerNameRow[]>([]);
  // Offered only when this server can reach Okta, and on by default then: adding a
  // person is the moment they should exist in the identity source. It is also what
  // gives them system access; unchecking it records them here only, without it, until
  // somebody grants it from their page.
  const [exportToOkta, setExportToOkta] = useState(viewer.oktaExportEnabled);

  // Names to show, codes to commit. Fed from the backend's own list, so the options
  // and the validation that will judge the choice cannot disagree.
  const countryOptions = useMemo(
    () => (lookups?.countries ?? []).map((c) => ({ value: c.code, label: c.name, hint: c.code })),
    [lookups]
  );

  // Only subcategories belonging to the chosen population: picking one from the
  // other side is the kind of mistake that silently routes a person into the wrong
  // database, and the backend rejects it anyway.
  const subcategories = useMemo(
    () => (lookups?.needNpdSubcategories ?? []).filter((s) => s.defaultPopulation === population),
    [lookups, population]
  );

  // Two engagement fields belong to a partner and not to an on-demand contractor: the
  // external partner organization is the firm a partner is engaged through, and the
  // title is the subcategory, which the backend fills in. Both are refused server-side
  // for this population, so hiding them keeps the form from offering what would be
  // rejected — the guarantee is the backend's, as ever.
  const isODC = population === "ON_DEMAND_CONTRACTOR";

  // Beam number and beam type belong to the Beam subcategory and to no other, so the
  // section follows the subcategory rather than the population. Refused server-side
  // anywhere else, as ever — this keeps the form from offering what would be rejected.
  const isBeam = isBeamSubcategory(subcategories.find((s) => s.id === subcategoryId));

  if (writable.length === 0) {
    return (
      <p className="text-sm text-slate-600">
        You hold read access only, so there is nothing here for you to create.
      </p>
    );
  }

  const emailMissing = !workEmail.trim() && !personalEmail.trim();
  const incomplete =
    emailMissing ||
    !entityId ||
    !subcategoryId ||
    !population ||
    (!isODC && (!title.trim() || !driId)) ||
    // The backend refuses a former name recorded without a name rather than dropping
    // it silently. Blocking here means the refusal is not a toast after the fact.
    rowsMissingAName(formerNames);

  return (
    // No card: this renders inside the People page's floating panel, which is
    // already a titled frame, and a second bordered box immediately inside the
    // first is a boundary that separates nothing. The submit sits after the fields
    // it commits rather than in a header above them, which is where it had to live
    // while a card was supplying the frame.
    <div>
      <p className="mb-3 text-xs text-slate-500">
        <span className="text-red-600">*</span> required. Everything else can be filled in later
        from the person's own page.
      </p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Field label="Population" required>
          <Select
            value={population}
            onChange={(e) => {
              setPopulation(e.target.value as Population);
              // The old subcategory belongs to the old population.
              setSubcategoryId("");
            }}
          >
            {writable.map((p) => (
              <option key={p} value={p}>
                {humanPopulation(p)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Legal first name">
          <Input value={legalFirstName} onChange={(e) => setLegalFirstName(e.target.value)} />
        </Field>
        <Field label="Legal middle name">
          <Input value={legalMiddleName} onChange={(e) => setLegalMiddleName(e.target.value)} />
        </Field>
        <Field label="Legal last name">
          <Input value={legalLastName} onChange={(e) => setLegalLastName(e.target.value)} />
        </Field>

        <Field label="Local legal name" hint="As written on the legal document">
          <Input
            value={legalFullNameLocal}
            onChange={(e) => setLegalFullNameLocal(e.target.value)}
          />
        </Field>
        <Field label="Preferred first name" hint="What they go by, if different">
          <Input
            value={preferredFirstName}
            onChange={(e) => setPreferredFirstName(e.target.value)}
          />
        </Field>
        <Field label="Preferred middle name">
          <Input
            value={preferredMiddleName}
            onChange={(e) => setPreferredMiddleName(e.target.value)}
          />
        </Field>

        <Field label="Preferred last name">
          <Input value={preferredLastName} onChange={(e) => setPreferredLastName(e.target.value)} />
        </Field>
        <Field label="Gender">
          {/* Matches the detail screen's control, so the two do not disagree about
              what the field accepts. */}
          <Select value={gender} onChange={(e) => setGender(e.target.value as Gender | "")}>
            <option value="">Not recorded</option>
            <option value="FEMALE">Female</option>
            <option value="MALE">Male</option>
          </Select>
        </Field>
        <Field label="Work country" hint="Where the person works, not where they live">
          <Combobox
            label="Work country"
            value={workLocation}
            options={countryOptions}
            onChange={setWorkLocation}
            emptyLabel="Not recorded"
            placeholder="Search countries…"
          />
        </Field>

        {/* One requirement met by either field, so neither carries a bare asterisk:
            that would read as "both required" and send people looking for a personal
            address they do not need. It is stated once, under the pair, and only while
            it is unmet — a standing note under each box described the pair to someone
            reading about one field, which made each box look as though it took either
            kind of address. The asterisk is the legend's, marking the requirement that
            no single field could carry.

            The wrapper spans two columns so the two boxes keep the cells they had and
            the message lands under both rather than in the next row's first cell. */}
        <div className="flex flex-col gap-1.5 md:col-span-2">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label="Work email">
              <Input value={workEmail} onChange={(e) => setWorkEmail(e.target.value)} />
            </Field>
            <Field label="Personal email">
              <Input value={personalEmail} onChange={(e) => setPersonalEmail(e.target.value)} />
            </Field>
          </div>
          {emailMissing ? (
            <p className="text-xs text-slate-500">
              <span className="text-red-600">*</span> Enter at least one email address — work or
              personal.
            </p>
          ) : null}
        </div>
        <FormerNameFields rows={formerNames} onChange={setFormerNames} />

        <Field label="Entity" hint="The contracting and paying entity" required>
          <Select value={entityId} onChange={(e) => setEntityId(e.target.value)}>
            <option value="">Choose…</option>
            {(lookups?.needEntities ?? []).map((entity) => (
              <option key={entity.id} value={entity.id}>
                {entity.code} — {entity.name}
              </option>
            ))}
          </Select>
        </Field>
        <DepartmentField
          departments={lookups?.departments}
          value={departmentId}
          onChange={setDepartmentId}
        />
        <Field label="Subcategory" required>
          <Select value={subcategoryId} onChange={(e) => setSubcategoryId(e.target.value)}>
            <option value="">Choose…</option>
            {subcategories.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
        {/* The inline form sits beside the Field rather than inside it: Field
            renders a <label>, and a label nested in a label sends clicks meant for
            the inner inputs to the outer Select. */}
        {isODC ? null : (
          <div className="flex flex-col gap-1.5">
            <Field label="Partner organization" hint="Blank for independent individuals">
              <Select
                value={partnerOrganizationId}
                onChange={(e) => setPartnerOrganizationId(e.target.value)}
              >
                <option value="">None</option>
                {assignableOrganizations(lookups?.externalPartnerOrganizations).map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </Select>
            </Field>
            <InlineCreateOrg onCreated={setPartnerOrganizationId} />
          </div>
        )}

        {isODC ? (
          // Said in place of the field rather than left blank, so its absence reads
          // as deliberate rather than as a control that failed to render.
          <p className="self-center text-xs text-slate-500">
            The title is the subcategory for an on-demand contractor, and is recorded from it.
          </p>
        ) : (
          <Field label="Title" required>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
        )}
        {isODC ? null : <PartnerDRIField value={driId} onChange={setDriId} />}
        <Field
          label="Contract link"
          hint="A link to the signed contract where it lives. Nothing is uploaded here."
        >
          <Input
            type="url"
            placeholder="https://…"
            value={contractLink}
            onChange={(e) => setContractLink(e.target.value)}
          />
        </Field>
        {isBeam ? (
          <BeamFields
            beamNumber={beamNumber}
            beamType={beamType}
            onBeamNumber={setBeamNumber}
            onBeamType={setBeamType}
          />
        ) : null}
        {/* No status field. Where a term sits in time is read from the dates below:
            a start date in the future is upcoming, today or earlier is active, and
            it changes over on the day with nothing written. Asking for it here
            would invite a stored answer that disagrees with the dates. */}
        <Field
          label="Start date"
          hint="Contract start, not the Slack join date. Leave blank if unknown. Leaving both dates blank means Active."
        >
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </Field>
        <Field label="Contracted end" hint="A fixed term's last day. Blank for open-ended.">
          {/* A future date here does not close the term — status decides that. This
              is how a fixed-term contract is recorded, which until now only the CSV
              importer could do. */}
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </Field>
      </div>
      {viewer.oktaExportEnabled ? (
        <label className="mt-4 flex items-start gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={exportToOkta}
            onChange={(event) => setExportToOkta(event.target.checked)}
          />
          <span>
            Export to Okta
            <span className="mt-0.5 block text-xs text-slate-500">
              Gives them system access and creates them in the identity source with this
              person&apos;s id. Work email is used when they have one. If Okta does not accept them,
              nobody is saved. Unchecked, they start without system access until somebody grants it
              from their page.
            </span>
          </span>
        </label>
      ) : null}
      <div className="mt-4 flex justify-end border-t border-slate-200 pt-3">
        <Button
          disabled={incomplete || create.isPending}
          onClick={() =>
            create.mutate(
              {
                identity: {
                  legalFirstName: legalFirstName || undefined,
                  legalMiddleName: legalMiddleName || undefined,
                  legalLastName: legalLastName || undefined,
                  legalFullNameLocal: legalFullNameLocal || undefined,
                  preferredFirstName: preferredFirstName || undefined,
                  preferredMiddleName: preferredMiddleName || undefined,
                  preferredLastName: preferredLastName || undefined,
                  gender: gender || undefined,
                  workLocation: workLocation || undefined,
                  workEmail: workEmail || undefined,
                  personalEmail: personalEmail || undefined
                },
                engagement: {
                  population: population as Population,
                  entityId,
                  departmentId: departmentId || undefined,
                  subcategoryId,
                  // Omitted by population rather than by whether the box was
                  // filled: switching to an on-demand contractor after typing a
                  // title must not send the value the form has stopped showing.
                  partnerOrganizationId: isODC ? undefined : partnerOrganizationId || undefined,
                  title: isODC ? undefined : title,
                  driId: isODC ? undefined : driId,
                  // Not omitted by population: a contract is signed either side. This
                  // is the field the master sheet had and this database did not, so
                  // somebody copying a row across has somewhere to put it at the
                  // moment they are looking at it.
                  contractLink: contractLink || undefined,
                  // By subcategory, not by whether the boxes were filled: a number
                  // typed before switching to another subcategory must not be sent
                  // for a form that has stopped showing it.
                  beamDetail: isBeam
                    ? { beamNumber: beamNumber || undefined, beamType: beamType || undefined }
                    : undefined,
                  startDate: startDate || undefined,
                  endDate: endDate || undefined
                },
                formerNameRecords: formerNamesInput(formerNames),
                exportToOkta
              },
              { onSuccess: (result) => onCreated(result.createIdentity.id) }
            )
          }
        >
          {create.isPending ? "Creating…" : "Create"}
        </Button>
      </div>
    </div>
  );
}

/**
 * The Beam section: the two fields a Beam's term records and no other term has.
 *
 * A section of its own rather than two more boxes in the grid, because that is what
 * they are — fields that exist for one subcategory. Shared by this form and the one
 * on a person's page, so the two cannot drift into offering different things.
 *
 * Neither field is required. A Beam is often onboarded before the number is issued,
 * and demanding it would push the whole term out of the record for the sake of a
 * value nobody has yet.
 */
/**
 * The former-name editor: one row per name, each saying what kind of name it was,
 * when it was the person's, and whether anyone checked it against a document.
 *
 * Rows rather than one semicolon-separated box, because the box could only ever hold
 * the strings. Order is meaning and not presentation — most recent first, as the
 * source sheet had it — so rows are appended and removed rather than sorted.
 *
 * Only the name is required of a row. Most former names arrive off a document at
 * onboarding with no idea when they stopped being current, and demanding the interval
 * would keep them out of the record entirely.
 */
/**
 * The five kinds, plus the answer that nothing is claimed.
 *
 * "Not recorded" is an option rather than the absence of one, because the field is
 * optional here and a radio set has no way back out. It leads, as it did while this
 * was a `<Select>` whose first option it was, so the answer selected on a fresh row
 * is the one at the top rather than one buried under five it is not.
 */
const FORMER_NAME_KIND_OPTIONS: readonly RadioOption<FormerNameRow["nameType"]>[] = [
  { value: "", label: "Not recorded" },
  ...FORMER_NAME_TYPES
];

export function FormerNameFields({
  rows,
  onChange
}: {
  rows: FormerNameRow[];
  onChange: (rows: FormerNameRow[]) => void;
}) {
  const fieldsRef = useRef<HTMLDivElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const focusAfterChange = useRef<number | "add" | null>(null);

  useEffect(() => {
    const target = focusAfterChange.current;
    if (target === null) return;
    if (target === "add") {
      addRef.current?.focus();
    } else {
      fieldsRef.current
        ?.querySelector<HTMLInputElement>(`input[aria-label="Former name ${target + 1}"]`)
        ?.focus();
    }
    focusAfterChange.current = null;
  }, [rows.length]);

  const update = (index: number, patch: Partial<FormerNameRow>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <div ref={fieldsRef} className="min-w-0 md:col-span-3">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Former names
      </h3>
      {rows.length > 0 ? (
        <p className="mb-2 text-xs text-slate-500">
          Most recent first. Only the name is needed — record the rest if it is known.
        </p>
      ) : null}
      <div className="flex flex-col gap-3">
        {rows.map((row, index) => (
          // Rows have no id of their own until they are saved, and the index is the
          // order, which is the meaning here.
          // biome-ignore lint/suspicious/noArrayIndexKey: the index is the row's identity
          <div
            key={index}
            className={cn(
              "min-w-0 flex flex-col gap-2",
              // A separator on every row but the first. Each row is two blocks tall
              // now, and the space between a row's own halves is about the space
              // between neighbouring rows — so three names read as one wall without
              // a line saying where each ends.
              index > 0 && "border-t border-slate-200 pt-3"
            )}
          >
            <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] [&>label]:min-w-0 [&_input]:min-w-0 [&_select]:min-w-0">
              {/* "Former name" rather than "Name": the inline add-an-organization form
                  on this same screen has a Name field of its own, and two controls
                  labelled Name for different things is ambiguous to a reader, not only
                  to a query. The aria-label numbers the row on top of that. */}
              <Field label="Former name">
                <Input
                  aria-label={`Former name ${index + 1}`}
                  value={row.fullName}
                  onChange={(e) => update(index, { fullName: e.target.value })}
                />
              </Field>
              <Field label="Valid from" hint="When the name began, not when it was recorded">
                <Input
                  type="date"
                  aria-label={`Former name ${index + 1} valid from`}
                  value={row.validFrom}
                  onChange={(e) => update(index, { validFrom: e.target.value })}
                />
              </Field>
              <Field label="Valid to" hint="The last day it was their name">
                <Input
                  type="date"
                  aria-label={`Former name ${index + 1} valid to`}
                  value={row.validTo}
                  onChange={(e) => update(index, { validTo: e.target.value })}
                />
              </Field>
              <Field label="Provenance">
                <Select
                  aria-label={`Former name ${index + 1} provenance`}
                  value={row.provenance}
                  onChange={(e) =>
                    update(index, { provenance: e.target.value as FormerNameRow["provenance"] })
                  }
                >
                  <option value="">Not recorded</option>
                  {NAME_PROVENANCES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Button
                variant="secondary"
                className="justify-self-start lg:mt-6 lg:self-start"
                aria-label={`Remove former name ${index + 1}`}
                onClick={() => {
                  focusAfterChange.current =
                    rows.length === 1 ? "add" : Math.min(index, rows.length - 2);
                  onChange(rows.filter((_, i) => i !== index));
                }}
              >
                Remove
              </Button>
            </div>
            {/* Lifted out of the grid, which is what gives the five kinds room to
                show what each one means. A writer cannot tell from the labels alone
                which applies — maiden is a kind of prior legal name, romanization a
                kind of transliteration — and a closed dropdown shows one and hides
                the other four. Radios rather than a `title` on the dropdown: a
                tooltip is invisible on touch and unreachable by keyboard, so it
                would render the hints without anyone being able to read them.

                The name is per row. Two groups sharing one are a single radio group
                to the browser, so answering the second name's kind would silently
                blank the first's. */}
            <RadioGroup
              name={`former-name-kind-${index}`}
              legend="Kind"
              value={row.nameType}
              options={FORMER_NAME_KIND_OPTIONS}
              onChange={(nameType) => update(index, { nameType })}
            />
          </div>
        ))}
      </div>
      {rowsMissingAName(rows) ? (
        <p className="mt-1 text-xs text-red-600">
          A former name needs a name. Fill it in, or remove the row.
        </p>
      ) : null}
      <Button
        variant="secondary"
        ref={addRef}
        className="mt-2"
        onClick={() => {
          focusAfterChange.current = rows.length;
          onChange([...rows, emptyFormerNameRow()]);
        }}
      >
        {rows.length === 0 ? "Add a former name" : "Add another former name"}
      </Button>
    </div>
  );
}

export function BeamFields({
  beamNumber,
  beamType,
  onBeamNumber,
  onBeamType
}: {
  beamNumber: string;
  beamType: BeamType | "";
  onBeamNumber: (value: string) => void;
  onBeamType: (value: BeamType | "") => void;
}) {
  return (
    <div className="md:col-span-3">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Beam</h3>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Field label="Beam number" hint="As issued. Can be filled in later.">
          {/* A text box, not a number one: leading zeros and prefixes are part of the
              value, and nothing ever counts or sums it. */}
          <Input value={beamNumber} onChange={(e) => onBeamNumber(e.target.value)} />
        </Field>
        <Field label="Beam type">
          <Select value={beamType} onChange={(e) => onBeamType(e.target.value as BeamType | "")}>
            <option value="">Not recorded</option>
            {BEAM_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </div>
  );
}
